import React, { useState, useEffect } from 'react';
import { Button, Card, Row, Col, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faInfoCircle,
  faFileContract,
  faCalendarCheck,
  faUsers,
  faExclamationTriangle,
  faEnvelope,
  faBolt,
  faComments,
  faPaperPlane
} from '@fortawesome/free-solid-svg-icons';
import dealChatbotPrompts from 'data/dealChatbotPrompts.json';
import './DealChatbot.css';

interface DealChatbotEnhancedProps {
  dealId: string;
  onSendMessage: (message: string) => void;
  isLoading?: boolean;
}

interface QuickAction {
  id: string;
  label: string;
  icon: string;
  prompt: string;
  category: string;
  cached: boolean;
  description: string;
}

interface Category {
  title: string;
  icon: string;
  prompts: string[];
}

type Categories = {
  [key: string]: Category;
}

const iconMap: { [key: string]: any } = {
  faInfoCircle,
  faFileContract,
  faCalendarCheck,
  faUsers,
  faExclamationTriangle,
  faEnvelope,
  faBolt,
  faComments
};

const DealChatbotEnhanced: React.FC<DealChatbotEnhancedProps> = ({
  dealId,
  onSendMessage,
  isLoading = false
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('overview');
  const [recentlyUsed, setRecentlyUsed] = useState<string[]>([]);
  const { quickActions, categories, suggestionTriggers, tooltips } = dealChatbotPrompts.dealChatbotPrompts;
  
  // Type assertion for categories
  const typedCategories = categories as Categories;

  // Track recently used prompts
  const handleQuickAction = (prompt: string, actionId: string) => {
    onSendMessage(prompt);
    
    // Update recently used
    setRecentlyUsed(prev => {
      const updated = [actionId, ...prev.filter(id => id !== actionId)].slice(0, 3);
      localStorage.setItem(`deal_${dealId}_recent_prompts`, JSON.stringify(updated));
      return updated;
    });
  };

  // Load recently used prompts
  useEffect(() => {
    const saved = localStorage.getItem(`deal_${dealId}_recent_prompts`);
    if (saved) {
      setRecentlyUsed(JSON.parse(saved));
    }
  }, [dealId]);

  // Get prompts for selected category
  const getCategoryPrompts = () => {
    const category = typedCategories[selectedCategory];
    if (!category) return [];
    
    return category.prompts.map((prompt: string) => {
      const quickAction = quickActions.find((qa: QuickAction) => qa.prompt === prompt);
      return {
        prompt,
        cached: quickAction?.cached || false,
        id: quickAction?.id || prompt
      };
    });
  };

  // Get recently used quick actions
  const getRecentActions = () => {
    return recentlyUsed
      .map(id => quickActions.find((qa: QuickAction) => qa.id === id))
      .filter(Boolean);
  };

  return (
    <div className="deal-chatbot-enhanced">
      {/* Quick Action Buttons */}
      <div className="quick-actions-section mb-3">
        <h6 className="text-muted mb-2">
          <FontAwesomeIcon icon={faBolt} className="me-2" />
          Quick Actions
        </h6>
        <Row className="g-2">
          {quickActions.slice(0, 6).map((action: QuickAction) => (
            <Col xs={6} md={4} key={action.id}>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    {action.description}
                    {action.cached && (
                      <div className="text-warning mt-1">
                        {tooltips.cached}
                      </div>
                    )}
                  </Tooltip>
                }
              >
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="w-100 quick-action-btn"
                  onClick={() => handleQuickAction(action.prompt, action.id)}
                  disabled={isLoading}
                >
                  <FontAwesomeIcon 
                    icon={iconMap[action.icon]} 
                    className="me-2" 
                  />
                  {action.label}
                  {action.cached && (
                    <Badge bg="warning" className="ms-2">⚡</Badge>
                  )}
                </Button>
              </OverlayTrigger>
            </Col>
          ))}
        </Row>
      </div>

      {/* Recently Used */}
      {recentlyUsed.length > 0 && (
        <div className="recently-used-section mb-3">
          <h6 className="text-muted mb-2">Recently Asked</h6>
          <div className="d-flex flex-wrap gap-2">
            {getRecentActions().map((action: any) => (
              <Button
                key={action.id}
                variant="light"
                size="sm"
                onClick={() => handleQuickAction(action.prompt, action.id)}
                disabled={isLoading}
              >
                <FontAwesomeIcon icon={iconMap[action.icon]} className="me-1" />
                {action.label}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Category Tabs */}
      <div className="category-tabs mb-3">
        <div className="d-flex flex-wrap gap-2">
          {Object.entries(typedCategories).map(([key, category]) => (
            <Button
              key={key}
              variant={selectedCategory === key ? 'primary' : 'outline-secondary'}
              size="sm"
              onClick={() => setSelectedCategory(key)}
            >
              <FontAwesomeIcon icon={iconMap[category.icon]} className="me-1" />
              {category.title}
            </Button>
          ))}
        </div>
      </div>

      {/* Category Prompts */}
      <Card className="category-prompts-card">
        <Card.Body>
          <h6 className="mb-3">
            {typedCategories[selectedCategory]?.title} Questions
          </h6>
          <div className="prompt-list">
            {getCategoryPrompts().map((item: any) => (
              <div
                key={item.id}
                className="prompt-item d-flex justify-content-between align-items-center p-2 rounded hover-bg-light cursor-pointer"
                onClick={() => handleQuickAction(item.prompt, item.id)}
              >
                <span className="text-truncate">{item.prompt}</span>
                <div>
                  {item.cached && (
                    <Badge bg="warning" className="ms-2">⚡ Fast</Badge>
                  )}
                  <FontAwesomeIcon 
                    icon={faPaperPlane} 
                    className="ms-2 text-primary"
                  />
                </div>
              </div>
            ))}
          </div>
        </Card.Body>
      </Card>

      {/* Contextual Suggestions */}
      <div className="contextual-suggestions mt-3">
        <small className="text-muted">
          <FontAwesomeIcon icon={faComments} className="me-1" />
          Try asking about deal status, parties involved, or upcoming tasks for instant answers!
        </small>
      </div>
    </div>
  );
};

export default DealChatbotEnhanced;